// We're getting a bunch of html from the server and shoving it into react here.
import * as React from 'react';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import classnames from 'classnames';

import {Api} from 'nutshell-core/api';

import {headTitle} from 'shells/document/head-title';

import {ClientRouteLegacyLinks} from '../../client-route-legacy-links';

import {AdminPopoverInfo} from './admin-popover-info';
import type {ServerTitleBarProps} from './setup-root';
import {SetupSpecialState} from './setup-special-state';

import styles from './setup-page.module.css';

const BLOCKLISTED_PATH = /^\/(google|office365)/;
const DISPLAY_LOADING_SPINNER_TIMEOUT = 3000;

type OwnProps = {
    viewUrl: string;
    pageTitle: string;
    viewParams: string;
    onViewRendered?: (arg1?: string | null | undefined) => void;
    portalledContent?: React.ComponentType<any>;
    onServerTitleBarChange?: (titleBarProps: ServerTitleBarProps) => void;
    userId?: string;
    backgroundOffWhite?: boolean;
};

type Props = OwnProps & ContextRouter;

type State = {
    serverMarkup: string | null | undefined;
    isStillLoadingServerMarkup: boolean;
    didRenderServerMarkup: boolean;
    hasError: boolean;
};

const viewApi = new Api('/');
const headers = new Headers({
    'Content-Type': 'application/xhtml+xml',
    accept: '*/*',
    'X-Requested-With': 'XMLHttpRequest',
});

export class SetupPageComponent extends React.Component<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            serverMarkup: null,
            didRenderServerMarkup: false,
            isStillLoadingServerMarkup: false,
            hasError: false,
        };
    }

    componentDidMount() {
        this.fetchNewMarkup();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (
            prevProps.viewUrl !== this.props.viewUrl ||
            prevProps.viewParams !== this.props.viewParams
        ) {
            this.fetchNewMarkup();
        }

        if (
            prevState.serverMarkup !== this.state.serverMarkup ||
            prevState.didRenderServerMarkup !== this.state.didRenderServerMarkup
        ) {
            if (this.state.didRenderServerMarkup) {
                // This is used to mount react into any of the markup that was grabbed from the server.
                // There's probably a better way to do this now, but up to now we have been using data-attributes
                // to pass information from the server into react, and in the interest of keeping changes minimal
                // for the time being, we'll keep it that way.
                if (typeof this.props.onViewRendered === 'function') {
                    this.props.onViewRendered(this.props.userId);
                }

                Nut.updateAppearances();
                Nut.preparePage();

                const adminTooltip = document.getElementById('admin-tool-tip-react-mount');
                if (adminTooltip) {
                    ReactDOM.render(<AdminPopoverInfo />, adminTooltip);
                }
            }
        }
    }

    render() {
        const PortalledContent = this.props.portalledContent;
        const setupPageClassName = classnames({
            'background-offwhite full-height': this.props.backgroundOffWhite,
        });

        return (
            <>
                <Helmet>
                    <title>{headTitle(this.props.pageTitle)}</title>
                </Helmet>
                {this.state.serverMarkup ? (
                    <ClientRouteLegacyLinks
                        history={this.props.history}
                        isClientRoutable={isClientRoutable}
                    >
                        {({interceptClicks}) => (
                            <div
                                ref={(ref) => {
                                    if (ref && !this.state.didRenderServerMarkup) {
                                        if (
                                            this.props.onServerTitleBarChange &&
                                            this.state.serverMarkup
                                        ) {
                                            const parser = new DOMParser();

                                            // Parse the text
                                            const doc = parser.parseFromString(
                                                this.state.serverMarkup,
                                                'text/html'
                                            );

                                            const phtmlTrojanHorse = doc.querySelector(
                                                'input[name="phtml-trojan-horse"]'
                                            );

                                            if (
                                                phtmlTrojanHorse &&
                                                this.props.onServerTitleBarChange
                                            ) {
                                                this.props.onServerTitleBarChange({
                                                    // @ts-expect-error
                                                    pageTitle: phtmlTrojanHorse.dataset.pageTitle,
                                                    pageSubtitle:
                                                        // @ts-expect-error
                                                        phtmlTrojanHorse.dataset.pageSubtitle,
                                                });
                                            }
                                        }

                                        this.setState({
                                            didRenderServerMarkup: true,
                                        });
                                    }
                                }}
                                dangerouslySetInnerHTML={{
                                    // @ts-expect-error
                                    __html: this.state.serverMarkup,
                                }}
                                onClick={interceptClicks}
                                className={classnames(setupPageClassName, styles['setup-page'])}
                            />
                        )}
                    </ClientRouteLegacyLinks>
                ) : (
                    this.renderSpecialState()
                )}
                {PortalledContent ? <PortalledContent /> : null}
            </>
        );
    }

    renderSpecialState = () => {
        if (this.state.hasError) {
            return <SetupSpecialState type='error' />;
        }

        // If we've deemed our loading time to be "too long"
        // we'll start displaying a loading message so the
        // user knows something is happening.
        if (this.state.isStillLoadingServerMarkup) {
            return <SetupSpecialState type='loading' />;
        }

        return null;
    };

    fetchNewMarkup = () => {
        this.setState({
            serverMarkup: null,
            hasError: false,
            isStillLoadingServerMarkup: false,
            didRenderServerMarkup: false,
        });

        setTimeout(() => {
            if (!this.state.serverMarkup) {
                this.setState({
                    isStillLoadingServerMarkup: true,
                });
            }
        }, DISPLAY_LOADING_SPINNER_TIMEOUT);

        // Strip possible trailing slash
        const baseUrl = this.props.viewUrl.replace(/\/$/, '');
        const url = `${baseUrl}.webchunk${this.props.viewParams}`;

        viewApi
            // @ts-expect-error
            .get(url, undefined, headers)
            .then((res) => {
                // @ts-expect-error
                if (res.redirected) {
                    // @ts-expect-error
                    window.location = res.url;
                }

                return res;
            })
            // @ts-expect-error
            .then((res) => res.text())
            .then((body) => {
                this.setState({
                    serverMarkup: body,
                    isStillLoadingServerMarkup: false,
                    hasError: false,
                });
            })
            .catch(() => {
                this.setState({
                    isStillLoadingServerMarkup: false,
                    hasError: true,
                });
            });
    };
}

// @ts-expect-error
export const SetupPage = withRouter<OwnProps>(SetupPageComponent);

const hasTarget = (anchor: HTMLAnchorElement) => anchor.target && anchor.target !== '_self';

const fileRegex = /\.[a-zA-Z0-9]{2,4}$/;

const isProbablyFile = (anchor: HTMLAnchorElement) =>
    anchor.pathname &&
    (fileRegex.test(anchor.pathname) || anchor.classList.contains('file-download'));

const isBlocklistedTarget = (anchor: HTMLAnchorElement) => BLOCKLISTED_PATH.test(anchor.pathname);

const isIntegrationEnableButton = (anchor: HTMLAnchorElement) =>
    anchor.classList.contains('action-enable');

const isClientRoutable = (anchor: HTMLAnchorElement) => {
    return (
        anchor &&
        !hasTarget(anchor) &&
        !isProbablyFile(anchor) &&
        !isBlocklistedTarget(anchor) &&
        !isIntegrationEnableButton(anchor)
    );
};
